import '@widesk-ui/styles/style.scss';
import '@/styles/style.scss';

import 'dayjs/locale/fr';

import AuthProvider              from '@widesk-ui/contexts/AuthContext';
import ConfigProvider            from 'antd/lib/config-provider';
import LoginCheckModel           from '@/models/services/fondations/LoginCheckModel';
import React                     from 'react';
import RouterProvider            from '@/RouterProvider';
import ThemeModeContext          from '@widesk-ui/contexts/ThemeModeContext';
import { ThemeMode }             from '@widesk-ui/contexts/ThemeModeContext';
import appStore                  from '@/stores/appStore';
import authStore                 from '@widesk-core/stores/authStore';
import dayjs                     from 'dayjs';
import errorsFr                  from '@/errorTranslations/fr.json';
import listFilterManagerStore    from '@widesk-ui/stores/ListFilterManagerStore';
import locale                    from 'antd/locale/fr_FR';
import localizedFormat           from 'dayjs/plugin/localizedFormat';
import theme                     from 'antd/lib/theme';
import utc                       from 'dayjs/plugin/utc';
import { Platform }              from '@/proto/proto';
import { setErrorsTranslations } from '@widesk-core/tools/getTextAndTranslateFromError';
import ApiModel                  from '@widesk-core/models/ApiModel';
import LocaleCollection          from '@widesk-core/models/LocaleCollection';
import cacheSystem               from '@widesk-core/models/apiCacheSystem';
import axios                     from 'axios';

import UserRights = Platform.Service.Fondations.UserRights;

dayjs.locale('fr');
dayjs.extend(localizedFormat);
dayjs.extend(utc);

setErrorsTranslations(errorsFr);

// Pour utiliser LocaleCollection par défault à la place de ApiCollection,
// Ce qui permettra aux traductions d'être fetch par défault pour les models compatibles (lang=getLocale())
ApiModel.getResolvableCollection = () => LocaleCollection;

const LOCALSTORAGE_THEME_KEY = 'theme_mode';

export default function App() {
	const [mode, setMode] = React.useState<ThemeMode>(
		localStorage.getItem(LOCALSTORAGE_THEME_KEY) as ThemeMode || 'light',
	);

	// On sauvegarde le mode en localStorage quand il change
	React.useEffect(() => localStorage.setItem(LOCALSTORAGE_THEME_KEY, mode), [mode]);

	return (
		<ThemeModeContext.Provider value={{ mode, setMode }}>
			<ConfigProvider
				locale={locale}
				theme={{
					algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
					token: { 
						colorPrimary: '#5E35B1', 
						Pagination: {
							showPrevNextJumpers: true,
						}
					} as any,
					components: {
						Form: {
							// Le label a par défaut la hauteur d'un input (32px) alors que son texte
							// n'en fait que 22, ce qui crée un vide entre le label et son champ.
							labelHeight: 22,
						},
					},
				}}
			>
				<AuthProvider
					onLogin={async (_username, _password) => {
						const loginCheckModel = new LoginCheckModel();
						await loginCheckModel.patch({ _password, _username, from: 1 }, { stringifyAttributes: true });

						const allowedRoleIdentifiers = [
							'ROLE_SHOP_SUPER_ADMIN',
							'ROLE_SHOP_ADMIN',
							'ROLE_SHOP_GES_ORDER',
							'ROLE_SHOP_GES_SHIPPING',
							'ROLE_SHOP_DISTRIBUTOR',
						];

						if (loginCheckModel.roleIdentifiers.every(identifier => !allowedRoleIdentifiers.includes(identifier))) {
							throw new Error(`Vous n'êtes pas autorisé à vous connecter sur cette interface.`);
						}

						return [loginCheckModel.token, loginCheckModel.refreshToken];
					}}
					onLogout={async () => {
						cacheSystem.clear();
						listFilterManagerStore.clear();
						appStore.user.clear();
					}}
					onLogged={async () => { // L'application charge tant que cette méthode n'est pas terminée
						authStore.setRefreshTokenRequest(async () => {
							const service = window.APP_CONFIG.services.find(service => service.id === 'fondations');

							const response = await axios.post(service?.configurations.api_endpoint + '/token/refresh', {
								refresh_token: authStore.refreshToken,
								from: 1,
							});

							return { token: response.data.token, refreshToken: response.data.refresh_token };
						});

						const protoUserRights = UserRights.decode(new TextEncoder().encode(authStore.tokenData['platform.token.owner']));
						const userId = protoUserRights.urn?.identifier;

						if (userId) {
							await appStore.user.setId(userId).fetch();
						}

						appStore.load();
					}}
				>
					<RouterProvider />
				</AuthProvider>
			</ConfigProvider>
		</ThemeModeContext.Provider>
	);
}
